import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Keeps the back-in-stock prompt on the selected variant. A variant change re-renders only the variant picker,
 * so the prompt is taken from the section it fetched. The sign-up form is not connected to a service yet, so a
 * valid submission goes nowhere.
 *
 * @extends {Component}
 */
class ProductNotifyMe extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const container = this.closest('.shopify-section, dialog');
    container?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /**
   * @param {SubmitEvent} event - The submit event.
   */
  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-notify-me-form]')) return;

    event.preventDefault();
  };

  /**
   * Re-renders the prompt for the selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        // A morphed element, such as `<main>` for a combined listing sibling, already carries the new prompt.
        if (!detail?.html || detail.morphElementSelector) return;

        const newPrompt = detail.html.querySelector(`product-notify-me[data-block-id="${this.dataset.blockId}"]`);
        if (!(newPrompt instanceof HTMLElement) || this.querySelector('dialog[open]')) return;

        morph(this, newPrompt, MORPH_OPTIONS);
        // The morph leaves the element's own attributes alone
        this.hidden = newPrompt.hidden;
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;

        console.warn('[product-notify-me] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-notify-me')) {
  customElements.define('product-notify-me', ProductNotifyMe);
}
